import { Component, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { QuestService } from '../../../services/quest.service';

interface Session {
  id: number;
  heading: string;
  description: string;
  image: string;
  audio: string;
  audioUrl?: string | null; // URL from backend for edit mode
  backendId?: string; // original session id from backend (for updates/deletes)
  imageFile?: File | null;
  audioFile?: File | null;
  isExpanded: boolean;
}

@Component({
  selector: 'app-create-mindfulness-session',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './create-mindfulness-session.component.html',
  styleUrl: './create-mindfulness-session.component.css'
})
export class CreateMindfulnessSessionComponent implements OnInit {
  @Output() sessionCreated = new EventEmitter<void>();
  @Output() backClicked = new EventEmitter<void>();
  @Output() formOpened = new EventEmitter<void>();

  showForm: boolean = false;
  
  // Dashboard Card Section
  mindfulnessName: string = '';
  sessionImagePreview: string = '';
  sessionImageFile: File | null = null;

  // Session Journey
  introductionExpanded: boolean = false;
  introductionDescription: string = '';
  introductionImagePreview: string = '';
  introductionImageFile: File | null = null;
  introductionAudioFile: File | null = null;
  introductionAudioName: string = '';
  introductionAudioUrl: string | null = null; // For edit mode - stores URL from backend

  sessions: Session[] = [];
  sessionCounter: number = 0;

  // Image constraints
  private readonly sessionImageMaxWidth = 351;
  private readonly sessionImageMaxHeight = 170;
  private readonly introImageWidthReq = 100;
  private readonly introImageHeightReq = 94;
  allSessions: any[] = [];
  recentSessions: any[] = [];
  showAllSessions: boolean = false;
  sessionSearchQuery: string = '';

  // Validation modal
  showValidationModal = false;
  validationErrors: string[] = [];

  // Success modal
  showSuccessModal = false;

  // Saving loader
  isSaving = false;

  // Edit mode
  isEditMode = false;
  sessionId: string | null = null;
  originalSessionData: any = null; // Store original data to track changes
  publishingSessionId: string | null = null; // Track session being (un)published
  deletedSessionIds: string[] = []; // Track removed session IDs for update
  
  // Confirmation modal state
  showPublishConfirmModal = false;
  sessionToPublish: any = null;
  isPublishing: boolean = false; // true for "Publish to All", false for "Back to Draft"
  
  // Preview modal state
  showPreviewModal = false;
  playingAudios: { [key: string]: boolean } = {};
  audioElements: { [key: string]: HTMLAudioElement } = {};
  audioElementUrls: { [key: string]: string } = {}; // Store the URL used to create each audio element
  audioBlobUrls: { [key: string]: string } = {}; // Store blob URLs for file objects to reuse them

  constructor(private questService: QuestService) {}

  ngOnInit(): void {
    this.loadSessions();
  }

  private loadSessions(): void {
    this.questService.getMindfulnessSessions().subscribe({
      next: (res) => {
        const list = Array.isArray(res) ? res : (res?.result || res?.items || []);
        this.allSessions = [...list].sort((a: any, b: any) => {
          const da = new Date(a.createdDateTime || a.createdDate || 0).getTime();
          const db = new Date(b.createdDateTime || b.createdDate || 0).getTime();
          return db - da;
        });
        this.recentSessions = this.allSessions.slice(0, 5);
      },
      error: (err) => {
        console.error('Error loading sessions list', err);
      },
    });
  }

  onCreateSessionClick(): void {
    this.isEditMode = false;
    this.sessionId = null;
    this.showForm = true;
    this.formOpened.emit();
    // Initialize with Session 1 when form is first shown
    if (this.sessions.length === 0) {
      this.addSession();
    }
  }

  onEditSessionClick(sessionId: string): void {
    this.isEditMode = true;
    this.sessionId = sessionId;
    this.loadSessionForEdit(sessionId);
  }

  // Helpers for state handling
  // Get filtered sessions based on search query
  get filteredSessions(): any[] {
    const sessionsToFilter = this.showAllSessions ? this.allSessions : this.recentSessions;
    if (!this.sessionSearchQuery || this.sessionSearchQuery.trim() === '') {
      return sessionsToFilter;
    }
    const query = this.sessionSearchQuery.toLowerCase().trim();
    return sessionsToFilter.filter((session: any) => 
      (session.name || '').toLowerCase().includes(query)
    );
  }

  isSessionPublished(session: any): boolean {
    const state = (session?.state || session?.status || session?.State || session?.Status || '').toLowerCase();
    return state === 'published';
  }

  onToggleSessionPublish(session: any): void {
    const sessionId = session?.id || session?._id;
    if (!sessionId) {
      return;
    }
    // Show confirmation modal
    this.sessionToPublish = session;
    this.isPublishing = !this.isSessionPublished(session); // true if currently draft (will publish), false if currently published (will draft)
    this.showPublishConfirmModal = true;
  }

  confirmPublishSession(): void {
    if (!this.sessionToPublish) {
      return;
    }
    const sessionId = this.sessionToPublish?.id || this.sessionToPublish?._id;
    if (!sessionId) {
      return;
    }
    const nextState = this.isPublishing ? 'Published' : 'Draft';
    const formData = new FormData();
    formData.append('State', nextState);

    this.publishingSessionId = sessionId;
    this.questService.updateMindfulnessSession(sessionId, formData).subscribe({
      next: () => {
        this.publishingSessionId = null;
        this.closePublishConfirmModal();
        this.loadSessions();
      },
      error: (err) => {
        console.error('Error updating mindfulness session state', err);
        this.publishingSessionId = null;
        alert('Failed to update session state. Please try again.');
      },
    });
  }

  closePublishConfirmModal(): void {
    this.showPublishConfirmModal = false;
    this.sessionToPublish = null;
    this.isPublishing = false;
  }

  openPreviewModal(): void {
    // Stop all playing audios before opening
    this.stopAllAudios();
    // Clear all cached audio elements so they get recreated with latest URLs
    this.audioElements = {};
    this.audioElementUrls = {};
    this.playingAudios = {};
    // Revoke old blob URLs and clear them
    Object.keys(this.audioBlobUrls).forEach(key => {
      if (this.audioBlobUrls[key]) {
        URL.revokeObjectURL(this.audioBlobUrls[key]);
      }
    });
    this.audioBlobUrls = {};
    this.showPreviewModal = true;
  }

  closePreviewModal(): void {
    this.stopAllAudios();
    this.showPreviewModal = false;
  }

  stopAllAudios(): void {
    Object.keys(this.audioElements).forEach(key => {
      if (this.audioElements[key]) {
        this.audioElements[key].pause();
        this.audioElements[key].currentTime = 0;
      }
    });
    this.playingAudios = {};
  }

  getAudioUrl(audioFile: File | null | undefined, audioUrl: string | null, audioName: string): string | null {
    // If there's a new file, create or reuse blob URL
    if (audioFile) {
      // Check if we already have a blob URL for this file
      if (!this.audioBlobUrls[audioName]) {
        this.audioBlobUrls[audioName] = URL.createObjectURL(audioFile);
      }
      return this.audioBlobUrls[audioName];
    }
    // Otherwise, use the URL (for edit mode with existing audio)
    if (audioUrl) {
      return audioUrl;
    }
    return null;
  }

  toggleAudio(audioUrl: string | null, id: string): void {
    if (!audioUrl) {
      return;
    }

    // Check if we need to create a new audio element or update existing one
    let audio = this.audioElements[id];
    const storedUrl = this.audioElementUrls[id];
    
    // If no audio element exists, or if the URL has changed, create/update it
    if (!audio || storedUrl !== audioUrl) {
      // If old audio exists, clean it up
      if (audio) {
        audio.pause();
        audio.src = '';
        audio.load();
        // Revoke old blob URL if it was a blob
        if (storedUrl && storedUrl.startsWith('blob:')) {
          URL.revokeObjectURL(storedUrl);
        }
      }
      
      // Create new audio element with the new URL
      audio = new Audio(audioUrl);
      this.audioElements[id] = audio;
      this.audioElementUrls[id] = audioUrl; // Store the URL we used
      
      audio.addEventListener('ended', () => {
        this.playingAudios[id] = false;
      });
    } else {
      // Use existing audio element
      audio = this.audioElements[id];
    }

    const isCurrentlyPlaying = !audio.paused && !audio.ended;

    if (isCurrentlyPlaying) {
      // Pause
      audio.pause();
      this.playingAudios[id] = false;
    } else {
      // Pause all other audios
      Object.keys(this.audioElements).forEach(key => {
        if (key !== id && this.audioElements[key] && !this.audioElements[key].paused) {
          this.audioElements[key].pause();
          this.playingAudios[key] = false;
        }
      });

      // Reset if ended
      if (audio.ended) {
        audio.currentTime = 0;
      }

      // Play
      audio.play().then(() => {
        this.playingAudios[id] = true;
      }).catch((error) => {
        console.error('Error playing audio:', error);
        this.playingAudios[id] = false;
      });
    }
  }

  isAudioPlaying(id: string): boolean {
    return this.playingAudios[id] === true;
  }

  // Helper methods to get preview URLs (prioritize new files over existing URLs)
  getPreviewImageUrl(imageFile: File | null, existingUrl: string | null): string | null {
    if (imageFile) {
      // If new file uploaded, create object URL
      return URL.createObjectURL(imageFile);
    }
    // Otherwise, use existing URL from backend
    return existingUrl || null;
  }

  getSessionPreviewImage(session: Session): string | null {
    if (session.imageFile) {
      return URL.createObjectURL(session.imageFile);
    }
    return session.image || null;
  }

  getSessionAudioUrl(session: Session): string | null {
    // If new audio file uploaded, create or reuse blob URL
    if (session.audioFile) {
      const audioId = 'session-' + session.id;
      // Check if we already have a blob URL for this file
      if (!this.audioBlobUrls[audioId]) {
        this.audioBlobUrls[audioId] = URL.createObjectURL(session.audioFile);
      }
      return this.audioBlobUrls[audioId];
    }
    // Otherwise, use existing URL from backend
    return session.audioUrl || null;
  }

  loadSessionForEdit(sessionId: string): void {
    this.questService.getMindfulnessSessions().subscribe({
      next: (res) => {
        const list = Array.isArray(res) ? res : (res?.result || res?.items || []);
        // Find the session by ID
        const session = list.find((s: any) => (s.id === sessionId || s._id === sessionId));
        if (!session) {
          alert('Session not found');
          return;
        }

        // Store original session data for change tracking
        this.originalSessionData = JSON.parse(JSON.stringify(session));

        // Reset form first
        this.resetForm(true);
        this.isEditMode = true;
        this.sessionId = sessionId;

        // Dashboard Card Section
        this.mindfulnessName = session.name || '';
        if (session.thumbnail) {
          this.sessionImagePreview = session.thumbnail;
        }

        // Introduction Section
        if (session.introduction) {
          this.introductionDescription = session.introduction.description || '';
          if (session.introduction.thumbnail) {
            this.introductionImagePreview = session.introduction.thumbnail;
          }
          if (session.introduction.media) {
            this.introductionAudioName = 'Audio file loaded';
            this.introductionAudioUrl = session.introduction.media; // Store URL for preview
          }
          this.introductionExpanded = true;
        }

        // Sessions
        if (session.sessions && Array.isArray(session.sessions)) {
          this.sessions = session.sessions.map((s: any, index: number) => {
            return {
              id: index + 1,
              heading: s.shortDescription || '',
              description: s.longDescription || '',
              image: s.thumbnail || '',
              audio: s.media || '', // Store the actual URL, not just a string
              audioUrl: s.media || null, // Store URL separately for preview
              backendId: s.id || s._id, // preserve backend session id
              imageFile: null,
              audioFile: null,
              isExpanded: false
            };
          });
          this.sessionCounter = this.sessions.length;
        } else {
          this.addSession();
        }

        this.showForm = true;
        this.formOpened.emit();
      },
      error: (err) => {
        console.error('Error loading session for edit', err);
        alert('Failed to load session. Please try again.');
      },
    });
  }

  onBackClick(): void {
    this.showForm = false;
    this.isEditMode = false;
    this.sessionId = null;
    this.originalSessionData = null;
    this.backClicked.emit();
    this.resetForm();
  }

  toggleIntroduction(): void {
    this.introductionExpanded = !this.introductionExpanded;
  }

  toggleSession(sessionId: number): void {
    const session = this.sessions.find(s => s.id === sessionId);
    if (session) {
      session.isExpanded = !session.isExpanded;
    }
  }

  addSession(): void {
    this.sessionCounter++;
    this.sessions.push({
      id: this.sessionCounter,
      heading: '',
      description: '',
      image: '',
      audio: '',
      isExpanded: false
    });
  }

  removeSession(sessionId: number): void {
    const index = this.sessions.findIndex(s => s.id === sessionId);
    if (index !== -1 && this.sessions.length > 1) {
      const session = this.sessions[index];
      // If editing an existing mindfulness session and this session exists in backend, track it for deletion
      if (this.isEditMode && session.backendId) {
        const backendIdStr = String(session.backendId);
        if (!this.deletedSessionIds.includes(backendIdStr)) {
          this.deletedSessionIds.push(backendIdStr);
        }
      }

      this.sessions.splice(index, 1);
      this.sessions.forEach((s, idx) => {
        s.id = idx + 1;
      });
      this.sessionCounter = this.sessions.length;
    }
  }


  private validateAndPreviewImage(
    file: File,
    options: { maxWidth: number; maxHeight: number; allowLessOrEqual: boolean },
    onValid: (preview: string) => void,
    inputElement?: HTMLInputElement
  ) {
    const img = new Image();
    const reader = new FileReader();
    
    const resetInput = () => {
      if (inputElement) {
        inputElement.value = '';
      }
    };
    
    reader.onload = (e: any) => {
      const dataUrl = e.target.result as string;
      img.onload = () => {
        const { maxWidth, maxHeight, allowLessOrEqual } = options;
        const isValid = allowLessOrEqual
          ? img.width <= maxWidth && img.height <= maxHeight
          : img.width === maxWidth && img.height === maxHeight;

        if (isValid) {
          onValid(dataUrl);
        } else {
          const conditionText = allowLessOrEqual
            ? `must be less than or equal to ${maxWidth}px (width) and ${maxHeight}px (height)`
            : `must be exactly ${maxWidth}px (width) and ${maxHeight}px (height)`;
          alert(`Image is not according to the given criterion: it ${conditionText}.`);
          resetInput();
        }
      };
      img.onerror = () => {
        alert('Error loading image. Please try again.');
        resetInput();
      };
      img.src = dataUrl;
    };
    reader.onerror = () => {
      alert('Error reading image file. Please try again.');
      resetInput();
    };
    reader.readAsDataURL(file);
  }

  private validateAudio(file: File, onValid: (name: string) => void, _inputElement?: HTMLInputElement) {
    onValid(file.name);
  }

  onMainSessionImageSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.validateAndPreviewImage(
        file,
        {
          maxWidth: this.sessionImageMaxWidth,
          maxHeight: this.sessionImageMaxHeight,
          allowLessOrEqual: true,
        },
        (preview) => {
          this.sessionImageFile = file;
          this.sessionImagePreview = preview;
        },
        event.target
      );
    }
  }

  onIntroductionImageSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.validateAndPreviewImage(
        file,
        {
          maxWidth: this.introImageWidthReq,
          maxHeight: this.introImageHeightReq,
          allowLessOrEqual: false,
        },
        (preview) => {
          this.introductionImageFile = file;
          this.introductionImagePreview = preview;
        },
        event.target
      );
    }
  }

  onIntroductionAudioSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.validateAudio(file, (name) => {
        // Revoke old blob URL if it exists
        if (this.audioBlobUrls['intro']) {
          URL.revokeObjectURL(this.audioBlobUrls['intro']);
        }
        this.introductionAudioFile = file;
        this.introductionAudioName = name;
        // Clear the old URL when new file is uploaded so preview uses new file
        this.introductionAudioUrl = null;
        // Clear the cached blob URL so a new one will be created
        delete this.audioBlobUrls['intro'];
        // Clear any existing audio element for this
        if (this.audioElements['intro']) {
          this.audioElements['intro'].pause();
          delete this.audioElements['intro'];
          delete this.audioElementUrls['intro'];
        }
      }, event.target);
    }
  }

  onSessionImageSelected(event: any, sessionId: number): void {
    const file = event.target.files[0];
    if (file) {
      const session = this.sessions.find(s => s.id === sessionId);
      if (session) {
        this.validateAndPreviewImage(
          file,
          {
            maxWidth: this.introImageWidthReq,
            maxHeight: this.introImageHeightReq,
            allowLessOrEqual: false,
          },
          (preview) => {
            session.image = preview;
            session.imageFile = file;
          },
          event.target
        );
      }
    }
  }

  onSessionAudioSelected(event: any, sessionId: number): void {
    const file = event.target.files[0];
    if (file) {
      const session = this.sessions.find(s => s.id === sessionId);
      if (session) {
        this.validateAudio(file, (name) => {
          const audioId = 'session-' + session.id;
          // Revoke old blob URL if it exists
          if (this.audioBlobUrls[audioId]) {
            URL.revokeObjectURL(this.audioBlobUrls[audioId]);
          }
          session.audio = name;
          session.audioFile = file;
          // Clear the old URL when new file is uploaded so preview uses new file
          session.audioUrl = null;
          // Clear the cached blob URL so a new one will be created
          delete this.audioBlobUrls[audioId];
          // Clear any existing audio element for this
          if (this.audioElements[audioId]) {
            this.audioElements[audioId].pause();
            delete this.audioElements[audioId];
            delete this.audioElementUrls[audioId];
          }
        }, event.target);
      }
    }
  }

  validateSessionForm(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate Dashboard Card Section
    if (!this.mindfulnessName || this.mindfulnessName.trim() === '') {
      errors.push('Mindfulness Name is required');
    }
    if (!this.sessionImageFile && !this.sessionImagePreview) {
      errors.push('Mindfulness Image is required');
    }

    // Introduction is optional - no validation required

    // Validate Sessions
    if (this.sessions.length === 0) {
      errors.push('At least one Session is required');
    } else {
      this.sessions.forEach((session, idx) => {
        const sessionNum = idx + 1;
        if (!session.heading || session.heading.trim() === '') {
          errors.push(`Session ${sessionNum} Heading is required`);
        }
        if (!session.description || session.description.trim() === '') {
          errors.push(`Session ${sessionNum} Description is required`);
        }
        if (!session.imageFile && !session.image) {
          errors.push(`Session ${sessionNum} Image is required`);
        }
        if (!session.audioFile && !session.audio) {
          errors.push(`Session ${sessionNum} Audio is required`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  saveSession(): void {
    // Validate all fields
    const validation = this.validateSessionForm();
    if (!validation.isValid) {
      this.validationErrors = validation.errors;
      this.showValidationModal = true;
      return;
    }

    this.isSaving = true;
    const formData = new FormData();

    if (this.isEditMode && this.originalSessionData) {
      // EDIT MODE: Only send changed fields
      const original = this.originalSessionData;

      if (this.mindfulnessName !== (original.name || '')) {
        formData.append('MindfulnessName', this.mindfulnessName);
      }

      if (this.sessionImageFile) {
        formData.append('MindfulnessImage', this.sessionImageFile, this.sessionImageFile.name);
      }

      // Introduction - optional, only send if has values
      if (this.introductionDescription && this.introductionDescription.trim() !== '') {
        if (this.introductionDescription !== (original.introduction?.description || '')) {
          formData.append('IntroductionDescription', this.introductionDescription);
        }
      }
      if (this.introductionImageFile) {
        formData.append('IntroductionImages', this.introductionImageFile, this.introductionImageFile.name);
      }
      if (this.introductionAudioFile) {
        formData.append('IntroductionVoice', this.introductionAudioFile, this.introductionAudioFile.name);
      }

      const originalSessions = original.sessions || [];

      // If we are only deleting sessions, do NOT send any Sessions[*] data
      if (this.deletedSessionIds.length === 0) {
        this.sessions.forEach((s, idx) => {
          const originalSession = originalSessions[idx];
          
          if (!originalSession || s.heading !== (originalSession.shortDescription || '')) {
            formData.append(`Sessions[${idx}].Heading`, s.heading);
          }
          
          if (!originalSession || s.description !== (originalSession.longDescription || '')) {
            formData.append(`Sessions[${idx}].Description`, s.description);
          }

          if (s.imageFile) {
            formData.append(`Session[${idx}].Images`, s.imageFile, s.imageFile.name);
          }
          if (s.audioFile) {
            formData.append(`Session[${idx}].Voice`, s.audioFile, s.audioFile.name);
          }
        });
      }

      // If any sessions were removed in edit mode, send their backend IDs
      if (this.deletedSessionIds.length > 0) {
        this.deletedSessionIds.forEach((id, idx) => {
          formData.append(`DeletedSessionIds[${idx}]`, id);
        });
      }

      console.log('Updating session payload (FormData) - only changed fields...');
      this.questService.updateMindfulnessSession(this.sessionId!, formData).subscribe({
        next: (res) => {
          console.log('Session updated successfully', res);
          this.isSaving = false;
          this.showSuccessModal = true;
        },
        error: (err) => {
          console.error('Error updating session', err);
          this.isSaving = false;
          alert('Failed to update session. Please try again.');
        },
      });
    } else {
      // CREATE MODE: Send all fields
      formData.append('MindfulnessName', this.mindfulnessName);

      if (this.sessionImageFile) {
        formData.append('MindfulnessImage', this.sessionImageFile, this.sessionImageFile.name);
      }

      // Introduction - optional, only send if has values
      if (this.introductionDescription && this.introductionDescription.trim() !== '') {
        formData.append('IntroductionDescription', this.introductionDescription);
      }
      if (this.introductionImageFile) {
        formData.append('IntroductionImages', this.introductionImageFile, this.introductionImageFile.name);
      }
      if (this.introductionAudioFile) {
        formData.append('IntroductionVoice', this.introductionAudioFile, this.introductionAudioFile.name);
      }

      this.sessions.forEach((s, idx) => {
        formData.append(`Sessions[${idx}].Heading`, s.heading);
        formData.append(`Sessions[${idx}].Description`, s.description);
        if (s.imageFile) {
          formData.append(`Session[${idx}].Images`, s.imageFile, s.imageFile.name);
        }
        if (s.audioFile) {
          formData.append(`Session[${idx}].Voice`, s.audioFile, s.audioFile.name);
        }
      });

      console.log('Creating session payload (FormData)...');
      this.questService.saveMindfulnessSession(formData).subscribe({
        next: (res) => {
          console.log('Session saved successfully', res);
          this.isSaving = false;
          this.showSuccessModal = true;
        },
        error: (err) => {
          console.error('Error saving session', err);
          this.isSaving = false;
          alert('Failed to save session. Please try again.');
        },
      });
    }
  }

  onSuccessModalOk(): void {
    this.showSuccessModal = false;
    this.resetForm();
    this.showForm = false;
    this.isEditMode = false;
    this.sessionId = null;
    this.originalSessionData = null;
    this.loadSessions();
    this.sessionCreated.emit();
  }

  resetForm(preserveEditMode: boolean = false): void {
    this.mindfulnessName = '';
    this.sessionImagePreview = '';
    this.sessionImageFile = null;
    this.introductionExpanded = false;
    this.introductionDescription = '';
    this.introductionImagePreview = '';
    this.introductionImageFile = null;
    this.introductionAudioFile = null;
    this.introductionAudioName = '';
    this.introductionAudioUrl = null;
    this.sessions = [];
    this.sessionCounter = 1;
    this.showValidationModal = false;
    this.validationErrors = [];
    this.deletedSessionIds = [];
    if (!preserveEditMode) {
      this.isEditMode = false;
      this.sessionId = null;
      this.originalSessionData = null;
    }
  }
}

